import { authenticatedInstance } from '../index/index.service';
import { extractServiceErrorMessage } from '../shared/service-error.util';
import type {
  ApprovalActionType,
  ApprovalPayload,
  ApprovalReason,
  ApprovalRequestView,
  ApprovalSessionHistoryPage,
} from 'shared-models/browser';

export type { ApprovalActionType, ApprovalPayload, ApprovalReason, ApprovalRequestView, ApprovalSessionHistoryPage, ApprovalStatus } from 'shared-models/browser';

class ApprovalsService {
  async create(
    tenantId: string,
    orderId: number,
    body: {
      actionType: ApprovalActionType;
      reason: ApprovalReason;
      note: string | null;
      payload: ApprovalPayload;
    },
    employeeSession: string,
  ): Promise<ApprovalRequestView> {
    try {
      const response = await authenticatedInstance.post<ApprovalRequestView>(
        `/approvals/tenant/${tenantId}/order/${orderId}`,
        body,
        { headers: { 'x-employee-session': employeeSession } },
      );
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'שליחת הבקשה נכשלה'));
    }
  }

  async listPending(tenantId: string, orderId: number, employeeSession: string): Promise<ApprovalRequestView[]> {
    try {
      const response = await authenticatedInstance.get<ApprovalRequestView[]>(
        `/approvals/tenant/${tenantId}/order/${orderId}/pending`,
        { headers: { 'x-employee-session': employeeSession } },
      );
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'טעינת הבקשות נכשלה'));
    }
  }

  async listAllPending(tenantId: string, employeeSession: string): Promise<ApprovalRequestView[]> {
    try {
      const response = await authenticatedInstance.get<ApprovalRequestView[]>(
        `/approvals/tenant/${tenantId}/pending`,
        { headers: { 'x-employee-session': employeeSession } },
      );
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'טעינת הבקשות נכשלה'));
    }
  }

  async listCurrentSessionHistory(
    tenantId: string,
    employeeSession: string,
    cursor: number | null,
  ): Promise<ApprovalSessionHistoryPage> {
    try {
      const params = new URLSearchParams({ limit: '50' });
      if (cursor !== null) {
        params.set('cursor', String(cursor));
      }
      const response = await authenticatedInstance.get<ApprovalSessionHistoryPage>(
        `/approvals/tenant/${tenantId}/current-session-history?${params.toString()}`,
        { headers: { 'x-employee-session': employeeSession } },
      );
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'טעינת היסטוריית הבקשות נכשלה'));
    }
  }

  async decide(
    tenantId: string,
    requestId: number,
    decision: 'approved' | 'denied',
    employeeSession: string,
  ): Promise<ApprovalRequestView> {
    try {
      const response = await authenticatedInstance.post<ApprovalRequestView>(
        `/approvals/tenant/${tenantId}/request/${requestId}/decide`,
        { decision },
        { headers: { 'x-employee-session': employeeSession } },
      );
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'העדכון נכשל'));
    }
  }

  // Admin dashboard has no employee session, so the deciding manager is supplied explicitly.
  async decideAsAdmin(
    tenantId: string,
    requestId: number,
    decision: 'approved' | 'denied',
    decidedByPk: number,
  ): Promise<ApprovalRequestView> {
    try {
      const response = await authenticatedInstance.post<ApprovalRequestView>(
        `/approvals/admin/tenant/${tenantId}/request/${requestId}/decide`,
        { decision, decidedByPk },
      );
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'העדכון נכשל'));
    }
  }
}

export const approvalsService = new ApprovalsService();
